import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useData } from '../context/DataContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ensureMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs.selectorAttrs || {}).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  if (attrs.content != null) el.setAttribute('content', attrs.content);
};

const ensureLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const absolutize = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

/**
 * Site-wide SEO injector. Reads dynamic `branding` + `seoHome` from DataContext
 * and updates <title> and key meta tags. On the home page, uses the configured
 * home meta; on inner pages, uses them as a fallback (allows pages to override
 * later by calling document.title themselves if needed).
 */
const SEOHead = () => {
  const { branding = {}, seoHome = {}, pwa: pwaConfig = {} } = useData() || {};
  const location = useLocation();

  useEffect(() => {
    const siteName = branding.site_name || 'TelanganaMart';
    const isHome = location.pathname === '/';
    // Detail pages (supplier / vendor / product) own their own title, description,
    // og:* and JSON-LD via the page-level `useSEO` hook. SEOHead should NOT
    // overwrite those values — its job is only the site-wide defaults +
    // favicon + theme-color, which we still apply below.
    const pageOwnsSEO = (
      location.pathname.startsWith('/supplier/') ||
      location.pathname.startsWith('/vendor/') ||
      location.pathname.startsWith('/product/')
    );

    const favicon = absolutize(branding.favicon_url || '');
    const themeColor = branding.primary_color || '#ee5e23';
    ensureMeta('meta[name="theme-color"]', { selectorAttrs: { name: 'theme-color' }, content: themeColor });
    if (favicon) ensureLink('icon', favicon);
    // Apple devices ignore `manifest.json#icons` — they need an explicit
    // `<link rel="apple-touch-icon">`. We update it from the PWA settings
    // (falls back to logo) so admins can change the iOS home-screen icon.
    const appleIcon = absolutize(
      (pwaConfig && (pwaConfig.apple_touch_icon_url || pwaConfig.icon_192_url)) ||
      branding.logo_url || ''
    );
    if (appleIcon) ensureLink('apple-touch-icon', appleIcon);

    if (pageOwnsSEO) return;  // leave title/og/description to useSEO

    const title = isHome
      ? (seoHome.home_title || `${siteName} - Telangana's Largest B2B Marketplace`)
      : siteName;
    document.title = title;

    const description = seoHome.home_description ||
      `${siteName} — Telangana's largest B2B marketplace. Find verified vendors and the best quotes from local suppliers within minutes.`;
    const keywords = seoHome.home_keywords || '';
    const ogTitle = seoHome.og_title || title;
    const ogDescription = seoHome.og_description || description;
    const ogImage = absolutize(seoHome.og_image || branding.logo_url || '');

    ensureMeta('meta[name="description"]', { selectorAttrs: { name: 'description' }, content: description });
    ensureMeta('meta[name="keywords"]', { selectorAttrs: { name: 'keywords' }, content: keywords });

    ensureMeta('meta[property="og:title"]', { selectorAttrs: { property: 'og:title' }, content: ogTitle });
    ensureMeta('meta[property="og:description"]', { selectorAttrs: { property: 'og:description' }, content: ogDescription });
    ensureMeta('meta[property="og:type"]', { selectorAttrs: { property: 'og:type' }, content: 'website' });
    if (ogImage) ensureMeta('meta[property="og:image"]', { selectorAttrs: { property: 'og:image' }, content: ogImage });

    ensureMeta('meta[name="twitter:card"]', { selectorAttrs: { name: 'twitter:card' }, content: 'summary_large_image' });
    ensureMeta('meta[name="twitter:title"]', { selectorAttrs: { name: 'twitter:title' }, content: ogTitle });
    ensureMeta('meta[name="twitter:description"]', { selectorAttrs: { name: 'twitter:description' }, content: ogDescription });
    if (ogImage) ensureMeta('meta[name="twitter:image"]', { selectorAttrs: { name: 'twitter:image' }, content: ogImage });
  }, [branding, seoHome, pwaConfig, location.pathname]);

  return null;
};

export default SEOHead;
